import React, { useContext, useMemo, useCallback, useState } from 'react';
import { BaseCompAction, capitalizeAll, ITEMS_PER_PAGE_20, SlideOutComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { MetricsReportSubscription } from '@moderepo/mode-internal-apis';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useRitzTranslation } from '../../utils/customHooks';
import { EntitySubscriptionsListingContainer } from '.';
import { EntitySubscriptionsListingContainerProps } from './EntitySubscriptionsListingContainer';


const useStyle = makeStyles(() => {
    return {
        // Override the header background
        subscriptionsTable: {
            '& [class*="MuiTableRow-head"]': {
                backgroundColor: 'transparent',
            },
        },
    };
}, { name: 'SelectEntitySubscriptionContainer', index: 1 });


interface SelectEntitySubscriptionContainerProps {
    readonly projectId: number;
    readonly onSubscriptionSelected: (subscription: MetricsReportSubscription)=> void;
}


/**
 * This Component is a Slide Out component used for asking the user to select a subscription. We will use the EntitySubscriptionsListingContainer
 * to display the subscriptions and when the user selected a subscription, we will call the handler and pass it the selected subscription
 */
export const SelectEntitySubscriptionContainer: React.FC<SelectEntitySubscriptionContainerProps> = (
    props: SelectEntitySubscriptionContainerProps,
) => {
    const { projectId } = props;
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);

    const classes = useStyle();

    // Local state to keep track of the current selected entity from the list
    const [selectedSubscription, setSelectedSubscription] = useState<MetricsReportSubscription | undefined>();


    /**
     * On entity selected from the list of entities, we will remember the selected entity in the local state but will not show the entity's
     * details until the user click the 'Show selected entity' button.
     */
    const onSubscriptionSelected = useCallback((sub: MetricsReportSubscription) => {
        setSelectedSubscription(sub);
    }, []);


    /**
     * Create an Array of primary actions for the Entity
     */
    const primaryActions = useMemo(() => {
        const actions: BaseCompAction[] = [{
            label   : capitalizeAll(trans('entity.action.select_subscription')),
            disabled: !selectedSubscription,
            onClick : () => {
                if (selectedSubscription) {
                    props.onSubscriptionSelected(selectedSubscription);
                }
            },
        }];
        return actions;
    }, [props, selectedSubscription, trans]);


    /**
     * Create secondary actions for the Entity
     */
    const secondaryActions = useMemo(() => {
        return undefined;
    }, []);



    const hiddenFields = useMemo((): EntitySubscriptionsListingContainerProps['hiddenFields'] => {
        return {
            id     : true,
            agentId: true,
        };
    }, []);


    /**
     * Create the content for the slide-out panel. The content will just going to be the EntitiesListingContainer
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <EntitySubscriptionsListingContainer
                className={clsx(classes.subscriptionsTable, 'transparent')}
                projectId={projectId}
                hiddenFields={hiddenFields}
                itemsPerPage={ITEMS_PER_PAGE_20}
                onSubscriptionSelectedHandler={onSubscriptionSelected}
            />
        );
    }, [classes.subscriptionsTable, hiddenFields, onSubscriptionSelected, projectId]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
